import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import styled, { keyframes } from 'styled-components';
import { createPortal } from 'react-dom';
import { theme } from '../theme';
import ScrollReveal, { FloatingShape } from './ScrollReveal';

// Animations
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

// Modal Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.9);
  backdrop-filter: blur(8px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: ${theme.zIndex.modal};
  padding: 2rem;
  animation: ${fadeIn} 0.3s ease-out;
`;

const ModalContent = styled.div`
  position: relative;
  max-width: 95%;
  max-height: 90vh;
  animation: ${fadeIn} 0.4s ease-out 0.1s both;
  
  img {
    max-width: 100%;
    max-height: 90vh;
    border-radius: ${theme.borderRadius.xl};
    box-shadow: ${theme.shadows['2xl']};
    border: 2px solid rgba(59, 130, 246, 0.3);
  }
`;

const CloseButton = styled.button`
    position: absolute;
  top: -50px;
    right: 0;
  width: 40px;
  height: 40px;
  background: ${theme.gradients.professional};
    border: none;
  border-radius: 50%;
    color: white;
  font-size: 1.75rem;
  line-height: 1;
    cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${theme.shadows.lg};
  transition: all ${theme.transitions.base};
  
  &:hover {
    transform: scale(1.1) rotate(90deg);
    box-shadow: ${theme.shadows.xl};
  }
  
  @media (max-width: 768px) {
    top: -60px;
    right: 50%;
    transform: translateX(50%);
    
    &:hover {
      transform: translateX(50%) scale(1.1) rotate(90deg);
    }
  }
`;

const Modal = ({ image, onClose }) => {
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  return createPortal(
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={e => e.stopPropagation()}>
        <CloseButton onClick={onClose}>&times;</CloseButton>
        <img src={image} alt="Impact showcase enlarged view" />
      </ModalContent>
    </ModalOverlay>,
    document.body
  );
};

// Section Components
const ShowcaseSection = styled.section`
  padding: 6rem 2rem;
  background: ${theme.colors.neutral[50]};
  background-image: 
    radial-gradient(at 0% 0%, ${theme.colors.secondary[50]} 0px, transparent 50%),
    radial-gradient(at 100% 100%, ${theme.colors.primary[50]} 0px, transparent 50%);
  position: relative;
  overflow: hidden;
  scroll-margin-top: 80px;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${theme.gradients.professional};
    opacity: 0.8;
  }

  @media (max-width: 768px) {
    padding: 4.5rem 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 3.5rem 1.25rem;
  }
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
  
  @media (max-width: 768px) {
    padding: 0 1.5rem;
  }
  
  @media (max-width: 480px) {
    padding: 0 1rem;
  }
`;

const SectionTitle = styled.h2`
  text-align: center;
  font-size: clamp(2rem, 4vw, 3rem);
  font-family: ${theme.typography.fontFamily.heading};
  font-weight: ${theme.typography.fontWeight.bold};
  margin-bottom: 1rem;
  color: ${theme.colors.neutral[900]};
  position: relative;
  animation: ${fadeIn} 0.6s ease-out;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -12px;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 4px;
    background: ${theme.gradients.professional};
    border-radius: ${theme.borderRadius.full};
  }
`;

const SectionSubtitle = styled.p`
  text-align: center;
  color: ${theme.colors.neutral[600]};
  font-size: ${theme.typography.fontSize.lg};
  margin-bottom: 4rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const CarouselWrapper = styled.div`
  position: relative;
  padding: 2rem 0;
  display: flex;
  align-items: center;
  gap: 2rem;

  @media (max-width: 768px) {
    gap: 1rem;
  }

  @media (max-width: 480px) {
    padding: 1.5rem 0 1rem;
  }
`;

const NavigationButton = styled.button`
  position: absolute;
  ${props => props.direction === 'left' ? 'left: -70px;' : 'right: -70px;'}
  top: 50%;
  transform: translateY(-50%);
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: ${theme.gradients.professional};
  border: none;
  color: white;
  font-size: 1.75rem;
  font-weight: ${theme.typography.fontWeight.bold};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${theme.shadows.lg};
  transition: all ${theme.transitions.base};
  z-index: 10;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
    transform: translateX(-100%);
    transition: transform 0.6s;
  }
  
  &:hover:not(:disabled) {
    transform: translateY(-50%) scale(1.1);
    box-shadow: ${theme.shadows.xl};
    
    &::before {
      transform: translateX(100%);
    }
  }
  
  &:active:not(:disabled) {
    transform: translateY(-50%) scale(0.95);
  }
  
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    background: ${theme.colors.neutral[300]};
  }

  @media (max-width: 1200px) {
    ${props => props.direction === 'left' ? 'left: -10px;' : 'right: -10px;'}
    width: 50px;
    height: 50px;
    font-size: 1.5rem;
  }

  @media (max-width: 768px) {
    width: 44px;
    height: 44px;
    font-size: 1.25rem;
    ${props => props.direction === 'left' ? 'left: -5px;' : 'right: -5px;'}
  }
`;

const CarouselContainer = styled.div`
  display: flex;
  gap: 2rem;
  overflow-x: auto;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  scroll-snap-type: x mandatory;
  padding: 1rem 0 2rem;
  cursor: grab;
  flex: 1;
  
  &:active {
    cursor: grabbing;
  }
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    height: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${theme.colors.neutral[200]};
    border-radius: ${theme.borderRadius.full};
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${theme.gradients.professional};
    border-radius: ${theme.borderRadius.full};
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, ${theme.colors.primary[700]}, ${theme.colors.secondary[600]});
  }
  
  @media (max-width: 768px) {
    gap: 1.5rem;
    padding: 1rem 0 1.5rem;
  }
  
  @media (max-width: 480px) {
    gap: 1.25rem;
  }
`;

const StoryCard = styled.div`
  position: relative;
  width: clamp(280px, 85vw, 380px);
  flex: 0 0 clamp(280px, 85vw, 380px);
  background: white;
  border-radius: ${theme.borderRadius.xl};
  box-shadow: ${theme.shadows.md};
  transition: all ${theme.transitions.slow};
  border: 1px solid ${theme.colors.neutral[200]};
  overflow: hidden;
  scroll-snap-align: center;
  display: flex;
  flex-direction: column;
  animation: ${slideIn} 0.6s ease-out ${props => props.index * 0.1}s both;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${theme.gradients.professional};
    transform: scaleX(0);
    transform-origin: left;
    transition: transform ${theme.transitions.slow};
  }
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: ${theme.shadows['2xl']};
    
    &::before {
      transform: scaleX(1);
    }
  }
  
  @media (hover: none) {
    &:hover {
      transform: none;
      box-shadow: ${theme.shadows.md};
    }
  }
  
  @media (max-width: 480px) {
    width: clamp(260px, 90vw, 320px);
    flex: 0 0 clamp(260px, 90vw, 320px);
  }
`;

const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 250px;
  overflow: hidden;
  background: ${theme.colors.neutral[100]};
  cursor: pointer;
  
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.05) 100%);
    z-index: 1;
  }
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform ${theme.transitions.slow};
  }
  
  &:hover img {
    transform: scale(1.05);
  }
  
  @media (max-width: 768px) {
    height: 200px;
  }
`;

const CardContent = styled.div`
  padding: 1.75rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  flex: 1;
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    gap: 0.875rem;
  }
  
  @media (max-width: 480px) {
    padding: 1.25rem;
    gap: 0.75rem;
  }
`;

const CardTitle = styled.h3`
  font-size: ${theme.typography.fontSize.xl};
  font-family: ${theme.typography.fontFamily.heading};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.neutral[900]};
  line-height: ${theme.typography.lineHeight.tight};
  margin: 0;
`;

const CardDescription = styled.p`
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.neutral[600]};
  line-height: ${theme.typography.lineHeight.relaxed};
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Testimonial = styled.blockquote`
  margin: 0;
  padding: 1rem;
  background: ${theme.colors.primary[50]};
  border-left: 3px solid ${theme.colors.primary[500]};
  border-radius: 0 ${theme.borderRadius.md} ${theme.borderRadius.md} 0;
  position: relative;
  
  &::before {
    content: '"';
    position: absolute;
    top: 0.5rem;
    left: 0.5rem;
    font-size: 2rem;
    color: ${theme.colors.primary[200]};
    font-family: Georgia, serif;
    line-height: 1;
  }
  
  p {
    font-size: ${theme.typography.fontSize.sm};
    font-style: italic;
    color: ${theme.colors.neutral[700]};
    line-height: ${theme.typography.lineHeight.relaxed};
    margin: 0 0 0.5rem 0;
    padding-left: 1.5rem;
  }
  
  cite {
    display: block;
    font-size: ${theme.typography.fontSize.xs};
    color: ${theme.colors.primary[600]};
    font-weight: ${theme.typography.fontWeight.medium};
    font-style: normal;
    text-align: right;
    padding-top: 0.5rem;
    border-top: 1px solid ${theme.colors.primary[100]};
  }
`;

const ScrollHint = styled.div`
  text-align: center;
  margin-top: 1.5rem;
  color: ${theme.colors.neutral[500]};
  font-size: ${theme.typography.fontSize.sm};
  opacity: 0.7;
  
  @media (max-width: 768px) {
    font-size: ${theme.typography.fontSize.xs};
    margin-top: 1.25rem;
  }

  @media (max-width: 480px) {
    display: none;
  }
`;

const NavigationDots = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 2rem;
`;

const Dot = styled.button`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: none;
  background: ${props => props.$active 
    ? theme.gradients.professional 
    : theme.colors.neutral[300]};
  cursor: pointer;
  transition: all ${theme.transitions.base};
  
  &:hover {
    transform: scale(1.2);
  }
`;

// Sample data
const impactStories = [
  {
    id: 0,
    title: 'Founded KaTEK — Robotics & AI Company (2025)',
    image: '/images/showcases/CEO_KaTEK.jpeg',
    description: 'Founded KaTEK as CEO, a technology company focused on Robotics, AI, and Intelligent Systems — turning years of engineering expertise into an entrepreneurial venture building real-world autonomous solutions.',
    testimonial: 'From engineer to entrepreneur — KaTEK represents the next chapter in building intelligent systems that bridge hardware, software, and AI to solve real-world challenges.',
    author: 'Kabil Mbarki, CEO & Founder of KaTEK'
  },
  {
    id: 1,
    title: 'Recognized by University & Institute Directors (2025)',
    image: '/images/showcases/directeur.jpg',
    description: 'Recognized by the Directors of the Higher Institute of Computer Science Mahdia (ISIMA) and the University of Monastir for contributions to Robotics, AI, and IT.',
    testimonial: 'Kabil\'s innovative drive and impactful work are highly valued at ISIMA and the University.',
    author: 'University Director'
  },
  {
    id: 2,
    title: 'EU Ambassador Giuseppe Perrone Endorses Autonomous Car Project',
    image: '/images/showcases/ambassador.png',
    description: 'Presented my autonomous electric car (graduation project) to the Ambassador of the European Union in Tunisia, H.E. Giuseppe Perrone, during his visit to Mahdia.',
    testimonial: 'Your autonomous electric car project is truly impressive and holds significant promise. I strongly encourage you to continue its development.',
    author: 'H.E. Giuseppe Perrone, EU Ambassador to Tunisia'
  },
  {
    id: 3,
    title: 'Honored by the General Secretary of ISIMA (2025)',
    image: '/images/showcases/secretaire_general.jpg',
    description: 'Received an honor from the General Secretary of the Higher Institute of Computer Science Mahdia (ISIMA) for active involvement in institute activities.',
    testimonial: 'Kabil\'s dedication and initiative have significantly enriched ISIMA. This recognition is well-deserved.',
    author: 'General Secretary, ISIMA'
  },
  {
    id: 4,
    title: 'ISIMA Recognition for IoT & Robotics (2024)',
    image: '/images/showcases/directeur2024.jpg',
    description: 'Awarded by the Director of the Higher Institute of Computer Science Mahdia (ISIMA) for demonstrating strong hard skills in Internet of Things (IoT) and Robotics.',
    testimonial: 'Kabil\'s expertise in IoT and Robotics is commendable. His hard skills contribute significantly to our innovative projects at ISIMA.',
    author: 'Director, ISIMA'
  },
  {
    id: 5,
    title: 'FabLab Mahdia Opening (2022): With Minister & Orange GM',
    image: '/images/showcases/ministre_technologie.jpeg',
    description: 'Participated in the official opening of FabLab Mahdia alongside the Minister of Technology and Communication and Orange General Director, presenting innovative IoT and Robotics projects.',
    testimonial: 'The innovative spirit and projects showcased at FabLab Mahdia, especially in IoT and Robotics, are a testament to the bright future of technology in the region.',
    author: 'Minister of Technology and Communication'
  }
];

const ImpactShowcase = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const containerRef = useRef(null);
  const autoPlayRef = useRef(null);
  const autoPlayTimeoutRef = useRef(null);

  const enableAutoPlay = useCallback(() => {
    if (autoPlayTimeoutRef.current) {
      clearTimeout(autoPlayTimeoutRef.current);
    }

    autoPlayTimeoutRef.current = setTimeout(() => {
      setIsAutoPlaying(true);
    }, 1500);
  }, []);

  const openModal = useCallback((image) => {
    setSelectedImage(image);
    document.body.style.overflow = 'hidden';
    setIsAutoPlaying(false); // Pause auto-play when modal opens
    if (autoPlayTimeoutRef.current) {
      clearTimeout(autoPlayTimeoutRef.current);
    }
  }, []);

  const closeModal = useCallback(() => {
    setSelectedImage(null);
    document.body.style.overflow = 'unset';
    enableAutoPlay();
  }, [enableAutoPlay]);

  const getCardElements = useCallback(() => {
    if (!containerRef.current) return [];
    return Array.from(containerRef.current.children).filter(
      (child) => child instanceof HTMLElement
    );
  }, []);

  // Navigation functions
  const scrollToCard = useCallback((index) => {
    if (!containerRef.current) return;
    const cards = getCardElements();
    const targetCard = cards[index];
    const container = containerRef.current;

    if (!targetCard || !container) return;

    const containerCenter = container.offsetWidth / 2;
    const cardCenter = targetCard.offsetLeft + targetCard.offsetWidth / 2;
    const targetScroll = cardCenter - containerCenter;

    container.scrollTo({
      left: targetScroll,
      behavior: 'smooth'
    });

    setActiveIndex(index);
  }, [getCardElements]);

  const goToNext = useCallback(() => {
    const nextIndex = (activeIndex + 1) % impactStories.length;
    scrollToCard(nextIndex);
  }, [activeIndex, scrollToCard]);

  const goToPrev = useCallback(() => {
    const prevIndex = activeIndex === 0 ? impactStories.length - 1 : activeIndex - 1;
    scrollToCard(prevIndex);
  }, [activeIndex, scrollToCard]);

  // Simplified drag scrolling
  const handleMouseDown = (e) => {
    setIsDragging(true);
    setStartX(e.pageX - containerRef.current.offsetLeft);
    setScrollLeft(containerRef.current.scrollLeft);
    containerRef.current.style.cursor = 'grabbing';
    setIsAutoPlaying(false); // Pause auto-play when dragging
    if (autoPlayRef.current) {
      clearInterval(autoPlayRef.current);
      }
    if (autoPlayTimeoutRef.current) {
      clearTimeout(autoPlayTimeoutRef.current);
        }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    if (containerRef.current) {
      containerRef.current.style.cursor = 'grab';
    }
    enableAutoPlay();
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - containerRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    containerRef.current.scrollLeft = scrollLeft - walk;
  };
    
  // Update active index on scroll
  const handleScroll = useCallback(() => {
    if (!containerRef.current) return;
    
    const container = containerRef.current;
    const cards = getCardElements();
    if (cards.length === 0) return;

    const containerCenter = container.scrollLeft + container.offsetWidth / 2;
    let closestIndex = 0;
    let smallestDistance = Number.POSITIVE_INFINITY;

    cards.forEach((card, index) => {
      const cardCenter = card.offsetLeft + card.offsetWidth / 2;
      const distance = Math.abs(containerCenter - cardCenter);

      if (distance < smallestDistance) {
        smallestDistance = distance;
        closestIndex = index;
      }
    });

    setActiveIndex(closestIndex);
  }, [getCardElements]);

  // Auto-play functionality
  useEffect(() => {
    if (autoPlayRef.current) {
      clearInterval(autoPlayRef.current);
    }

    if (!isAutoPlaying) {
      return;
    }
    
    autoPlayRef.current = setInterval(() => {
      goToNext();
    }, 5000); // Change slide every 5 seconds

    return () => {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
      }
    };
  }, [isAutoPlaying, goToNext]);
      
  // Pause auto-play on hover
  const handleMouseEnter = () => {
    if (autoPlayRef.current) {
      clearInterval(autoPlayRef.current);
    }
    if (autoPlayTimeoutRef.current) {
      clearTimeout(autoPlayTimeoutRef.current);
        }
    setIsAutoPlaying(false);
  };

  const handleMouseLeave = () => {
    enableAutoPlay();
  };

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    
    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    return () => {
      if (autoPlayTimeoutRef.current) {
        clearTimeout(autoPlayTimeoutRef.current);
      }
    };
  }, []);

  return (
    <ShowcaseSection id="impact-showcase">
      <FloatingShape $top="6%" $left="4%" $size="65px" $shape="circle" $gradient="linear-gradient(135deg, rgba(245,158,11,0.07), rgba(217,70,239,0.07))" $duration="13s" $delay="2s" />
      <FloatingShape $bottom="8%" $right="3%" $size="50px" $shape="rounded" $gradient="linear-gradient(135deg, rgba(59,130,246,0.06), rgba(16,185,129,0.06))" $duration="17s" $delay="0s" $rotation="60deg" />
      <Container>
      <ScrollReveal direction="zoom" duration="0.9s">
        <SectionTitle>Impact Showcase</SectionTitle>
        <SectionSubtitle>
          Recognized achievements and collaborations with industry leaders
        </SectionSubtitle>
      </ScrollReveal>

        <CarouselWrapper>
          <NavigationButton
            direction="left"
            onClick={goToPrev}
            aria-label="Previous achievement"
            title="Previous (←)"
          >
            ‹
          </NavigationButton>

      <CarouselContainer 
        ref={containerRef}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseMove={handleMouseMove}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={() => {
              handleMouseUp();
              handleMouseLeave();
            }}
          >
            {impactStories.map((story, index) => (
              <StoryCard key={story.id} index={index}>
                <ImageWrapper onClick={() => openModal(story.image)}>
                <img 
                  src={story.image} 
                  alt={story.title} 
                    loading="lazy"
                />
                </ImageWrapper>
                <CardContent>
                  <CardTitle>{story.title}</CardTitle>
                  <CardDescription>{story.description}</CardDescription>
                  <Testimonial>
                    <p>{story.testimonial}</p>
                    <cite>— {story.author}</cite>
                  </Testimonial>
                </CardContent>
              </StoryCard>
          ))}
          </CarouselContainer>

          <NavigationButton
            direction="right"
            onClick={goToNext}
            aria-label="Next achievement"
            title="Next (→)"
          >
            ›
          </NavigationButton>
        </CarouselWrapper>

        <NavigationDots>
          {impactStories.map((_, index) => (
            <Dot
              key={index}
              $active={index === activeIndex}
              onClick={() => scrollToCard(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </NavigationDots>

        <ScrollHint>
          ← Drag to explore or use arrows →
        </ScrollHint>
      </Container>

      {selectedImage && <Modal image={selectedImage} onClose={closeModal} />}
    </ShowcaseSection>
  );
};

export default ImpactShowcase;
